import type { App } from 'antd';
import type { TFunction } from 'i18next';
import type { FeedbackApi } from '../../utils/feedback';
import type { OrderTransportsDto } from '../../dto/transport';
import { getApiErrorMessage } from '../../utils/apiError';
import { unlinkOrderFromTransport } from './api/orderTransportApi';
import { updateTransportRouteAndRefetch } from '../transports/api/transportRoutesApi';
import { submitWithOperationalException } from '../operationalExceptions/operationalExceptionFlow';
import { getTransportStatusDisplayTranslationKey } from '../transports/transportRouteViews';
import { getTransportErrorMessage, isBlockingTransportError } from '../transports/transportErrorMessages';
import type { TransportDeliveryFormItem } from '../transports/components/TransportDeliveryQuantityFields';
import { refreshOrderTransportReadModels } from './orderTransportMutation';

interface Dependencies {
  id?: string;
  modal: ReturnType<typeof App.useApp>['modal'];
  t: TFunction;
  feedback: FeedbackApi;
  orderTransports: OrderTransportsDto;
  refreshOrderTransports: () => Promise<unknown>;
  refreshOrderFinancials: () => Promise<unknown>;
  refreshSettlementReadiness: () => Promise<unknown>;
}

/** Created from the current render: no retained route id, DTO or global cache. */
export function createOrderTransportActions({
  id, modal, t, feedback, orderTransports,
  refreshOrderTransports, refreshOrderFinancials, refreshSettlementReadiness,
}: Dependencies) {
  // Previously this called DELETE /transport-routes/:id which removed the
  // whole transport — including any other orders riding on it. Transports
  // are N:N; unlinking one order must not cascade. Now we call the unlink
  // endpoint, which only drops the join row for this order. Backend still
  // cleans up the transport when it becomes empty (returns { deleted: true }).
  const handleUnlinkTransport = async (transportId: string) => {
    if (!id) return;
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('transport:orderTransports.confirmUnlinkTitle'),
        content: t('transport:orderTransports.confirmUnlinkContent'),
        okText: t('transport:orderTransports.unlink'),
        okType: 'danger',
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    try {
      await unlinkOrderFromTransport(transportId, id);
      feedback.success(t('transport:orderTransports.unlinkSuccess'));
      await refreshOrderTransportReadModels({
        kind: 'unlink', transports: refreshOrderTransports,
        financials: refreshOrderFinancials, readiness: refreshSettlementReadiness,
      });
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'transport:orderTransports.unlinkError'));
    }
  };

  const handleTransportStatusChange = async (
    transportId: string,
    payload: {
      status: string;
      actualDelivery?: string | null;
      deliveryItems?: TransportDeliveryFormItem[];
    },
  ): Promise<boolean> => {
    const transport = orderTransports.transports.find((item) => item.id === transportId);
    try {
      const refreshedTransport = await submitWithOperationalException(
        (confirmedPayload) => updateTransportRouteAndRefetch(transportId, confirmedPayload),
        payload,
        modal,
        t,
      );
      if (!refreshedTransport) return false;
      // Transport status changes physical delivery only. It must not mutate
      // or imply a financial settlement; the company balance changes later,
      // through the explicit partial/final settlement command.
      await refreshOrderTransportReadModels({
        kind: 'status', transports: refreshOrderTransports, readiness: refreshSettlementReadiness,
      });
      feedback.success(t('transport:statusUpdatedSuccessfully', {
        number: transport?.routeNumber,
        status: t(getTransportStatusDisplayTranslationKey(refreshedTransport.status)),
      }));
      return true;
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:errorUpdatingStatus', { number: transport?.routeNumber }),
        { sticky: isBlockingTransportError(error) },
      );
      return false;
    }
  };

  return { handleUnlinkTransport, handleTransportStatusChange };
}
